/**
 * Pure helper for the v1 → v2 offline-queue migration in CustomWebsocketProvider.
 *
 * The migration reads the shared v1 record once (a page-load snapshot) and copies
 * it into the per-tab v2 store. Cleanup must then remove ONLY the entries it
 * actually migrated — never the whole record — because a still-open old (v1) tab
 * can append fresh unsent edits after the snapshot, and deleting the whole record
 * would wipe those unread. This keeps such entries so they migrate on the next
 * load (idempotently, by updateId).
 */
export interface LegacyQueueRecord {
  roomId?: string;
  pending?: Array<{ updateId?: string } & Record<string, unknown>>;
  unacked?: Array<{ updateId?: string } & Record<string, unknown>>;
  updatedAt?: number;
  [key: string]: unknown;
}

export interface PrunedLegacyQueue {
  pending: LegacyQueueRecord['pending'];
  unacked: LegacyQueueRecord['unacked'];
  /** True when nothing survives, so the record can be deleted outright. */
  empty: boolean;
}

/**
 * Drop only the entries whose `updateId` was migrated; keep everything else
 * (concurrent appends from another tab, and any entry without an updateId).
 */
export function pruneMigratedEntries(
  record: LegacyQueueRecord | null | undefined,
  migratedIds: Set<string>
): PrunedLegacyQueue {
  const keep = (arr: LegacyQueueRecord['pending']): LegacyQueueRecord['pending'] =>
    (arr || []).filter((entry) => !(entry && entry.updateId && migratedIds.has(entry.updateId)));
  const pending = keep(record?.pending);
  const unacked = keep(record?.unacked);
  return { pending, unacked, empty: pending!.length === 0 && unacked!.length === 0 };
}

/** The set of updateIds a v1 record carried — what a successful migration copied. */
export function migratedUpdateIds(record: LegacyQueueRecord | null | undefined): Set<string> {
  const ids = new Set<string>();
  for (const entry of [...(record?.pending || []), ...(record?.unacked || [])]) {
    if (entry && entry.updateId) ids.add(entry.updateId);
  }
  return ids;
}
